import React from 'react'
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  CardMedia,
  Divider,
} from '@mui/material'
import AccessTimeFilledIcon from '@mui/icons-material/AccessTimeFilled'
import headerBgImage from '../../../assets/images/arbol-ec/fondos/020.jpg'
import { useTheme } from '@mui/material/styles'
import { useNavigate } from 'react-router-dom'
import { MAIN } from '../../../config/routes'
import { newsData, whatsNews, joinus } from '../../../utils/consumo'
import { getNewsEnabled } from '../../../services'
import BottomCustom from '../../BottonCustom'

const decodeFoto = foto => {
  if (!foto) return null
  if (typeof foto === 'string') return foto
  if (foto?.data) {
    try {
      const bytes = new Uint8Array(foto.data)
      return new TextDecoder().decode(bytes)
    } catch (error) {
      return null
    }
  }
  return null
}

const formatDate = dateValue => {
  if (!dateValue) return ''
  const date = new Date(dateValue)
  if (Number.isNaN(date.getTime())) return dateValue
  return date.toISOString().split('T')[0]
}

const truncateText = (text, maxLength = 220) => {
  if (!text) return ''
  return text.length > maxLength ? `${text.slice(0, maxLength).trim()}...` : text
}

const NewsSection = () => {
  const theme = useTheme()
  const navigate = useNavigate()
  const [newsList, setNewsList] = React.useState(newsData)

  React.useEffect(() => {
    let mounted = true
    getNewsEnabled()
      .then(items => {
        if (!mounted) return
        const normalized = items.map(item => ({
          title: item?.titulo || '',
          image: decodeFoto(item?.foto) || headerBgImage,
          date: formatDate(item?.fechaRegistro),
          city: item?.ciudad || 'General',
          category: item?.categoria || 'Noticia',
          description: truncateText(item?.resumen || item?.contenido || ''),
          link: item?.url || '#',
        }))
        if (normalized.length > 0) setNewsList(normalized)
      })
      .catch(() => {
        if (!mounted) return
        setNewsList(newsData)
      })
    return () => {
      mounted = false
    }
  }, [])

  const handleOnClick = () => navigate(MAIN)
  return (
    <Box sx={{ fontFamily: theme.typography.fontFamily }}>
      <Box
        sx={{
          backgroundImage: `url(${headerBgImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'top',
          position: 'relative',
          color: theme.palette.text.ivory,
          mt: '5.8vh',
          height: '40vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
        }}>
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundColor: `${theme.palette.text.darkBlue}AA`,
          }}
        />
        <Box sx={{ position: 'relative', zIndex: 1, px: { xs: 4, md: 5 } }}>
          <Typography
            variant="body1"
            sx={{
              maxWidth: 700,
              color: theme.palette.text.white,
              fontWeight: 600,
              fontSize: '1.2rem',
              textShadow: '0 0 1px rgba(255, 255, 255, 0.8)',
            }}>
            Conoce las acciones más recientes que impulsan el manejo sostenible del
            arbolado urbano y descubre cómo los árboles transforman nuestras ciudades.
          </Typography>
        </Box>
      </Box>

      <Box pb={4} pt={{ xs: 1, md: 2 }} px={{ xs: 5, md: 8, lg: 10 }}>
        <Box component="nav" mt={3}>
          <Box
            component="ol"
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              listStyle: 'none',
              justifyContent: 'end',
              p: 0,
              m: 0,
              fontSize: 16,
              fontWeight: 400,
            }}>
            <Box component="li">
              <Button
                onClick={handleOnClick}
                variant="text"
                sx={theme => ({
                  fontSize: 16,
                  fontWeight: 600,
                  color: theme.palette.text.mustard,
                  p: 0,
                  px: 2,
                  minWidth: 0,
                  '&:hover': {
                    color: theme.palette.text.primary,
                  },
                })}>
                Inicio
              </Button>
            </Box>
            <Box
              component="li"
              sx={{
                pl: 1,
                position: 'relative',
                fontWeight: 500,
                color: theme.palette.text.secondary,
              }}>
              <Box component="span" sx={{ pr: 1 }}>
                /
              </Box>{' '}
              Noticias
            </Box>
          </Box>
        </Box>

        {/* Trending News */}
        <Divider />
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            borderLeft: `4px solid ${theme.palette.secondary.main}`,
            pl: 4,
          }}>
          <Typography
            variant="h2"
            gutterBottom
            fontWeight={600}
            sx={{ color: theme.palette.text.forestGreen, my: 2 }}>
            Noticias
          </Typography>
        </Box>
        <Divider textAlign="right">
          <Typography
            variant="h4"
            gutterBottom
            fontWeight={600}
            sx={{ color: theme.palette.text.forestGreen, mt: 2, mb: 4 }}>
            Noticias Destacadas
          </Typography>
        </Divider>
        <Grid container spacing={3}>
          {newsList.map((news, idx) => (
            <Grid item xs={12} sm={6} md={4} key={idx}>
              <Card
                component="a"
                href={news.link}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  boxShadow: 0,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  textDecoration: 'none',
                  cursor: 'pointer',
                  '&:hover': {
                    color: theme.palette.text.primary,
                  },
                  '&:hover img': {
                    transform: 'scale(1.05)',
                  },
                }}>
                <Box sx={{ position: 'relative' }}>
                  <CardMedia
                    component="img"
                    height="180"
                    image={news.image}
                    alt={news.title}
                    sx={{
                      borderRadius: 1,
                      boxShadow:
                        '0px 8px 25px rgba(0, 0, 0, 0.25), 0px 5px 10px rgba(0, 0, 0, 0.22)',
                      transition: 'transform 0.3s ease-in-out',
                      '&:hover': {
                        color: theme.palette.primary.main,
                      },
                      width: '100%',
                    }}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 8,
                      left: 8,
                      backgroundColor: theme.palette.primary.main,
                      color: theme.palette.primary.contrastText,
                      px: 1,
                      borderRadius: 1,
                      fontSize: 12,
                      fontWeight: 500,
                      textTransform: 'uppercase',
                    }}>
                    {news.category}
                  </Box>
                </Box>

                <CardContent
                  sx={{
                    px: 0,
                    py: 0,
                    '&:hover': {
                      color: theme.palette.primary.main,
                    },
                  }}>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="end"
                    gap={1}
                    mb={2}
                    mt={2}
                    bgcolor={theme.palette.background.var_chip_light}>
                    <AccessTimeFilledIcon
                      sx={{
                        fontSize: 16,
                        color: theme.palette.text.mutedTeal,
                      }}
                    />
                    <Typography variant="body2" color={theme.palette.text.forestGreen}>
                      {news.date} | {news.city}
                    </Typography>
                  </Box>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{
                      color: theme.palette.primary.main,
                      fontWeight: 600,
                      lineHeight: 1.2,
                      '&:hover': {
                        color: theme.palette.secondary.main,
                      },
                    }}>
                    {news.title}
                  </Typography>
                  <Typography variant="body2">{news.description}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* What's New */}
        <Box>
          <Divider textAlign="right">
            <Typography
              variant="h4"
              gutterBottom
              fontWeight={600}
              sx={{ color: theme.palette.text.darkBlue, mt: 2, mb: 4 }}>
              Novedades
            </Typography>
          </Divider>
          <Grid container spacing={3}>
            {whatsNews.map((news, idx) => (
              <Grid item xs={12} sm={12} md={6} key={idx}>
                <Card
                  sx={{
                    display: 'flex',
                    height: '100%',
                    boxShadow: 3,
                    '&:hover .title-hover': {
                      color: theme.palette.secondary.main,
                    },
                    '&:hover img': {
                      transform: 'scale(1.05)',
                    },
                  }}>
                  <CardMedia
                    component="img"
                    image={news.image}
                    alt="Apadrina un Árbol"
                    sx={{
                      width: 130,
                      objectFit: 'cover',
                      borderRadius: '0',
                      transition: 'transform 0.3s ease-in-out',
                    }}
                  />
                  <Box
                    sx={{
                      flex: 1,
                      p: 2,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                    }}>
                    <Typography
                      variant="h6"
                      className="title-hover"
                      gutterBottom
                      sx={{
                        color: theme.palette.primary.main,
                        fontWeight: 600,
                        lineHeight: 1.2,
                      }}>
                      {news.title}
                    </Typography>
                    <Typography variant="body2">{news.description}</Typography>
                    {news.botton && (
                      <Box display="flex" justifyContent="end">
                        <BottomCustom
                          styleButton={{ mt: 2, width: 'fit-content' }}
                          text={news.botton.label}
                          onClick={() => navigate(news.botton.redirect)}
                        />
                      </Box>
                    )}
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Join Us */}
        <Box mt={6}>
          <Divider textAlign="center">
            <Typography
              variant="h4"
              gutterBottom
              fontWeight={600}
              sx={{ color: theme.palette.text.darkBlue, mt: 2, mb: 4 }}>
              Súmate
            </Typography>
          </Divider>
          <Grid container spacing={3}>
            {joinus.map((news, idx) => (
              <Grid item xs={12} sm={12} md={6} key={idx}>
                <Card
                  sx={{
                    display: 'flex',
                    height: '100%',
                    boxShadow: 3,
                  }}>
                  <Box
                    sx={{
                      flex: 1,
                      p: 2,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                    }}>
                    <Typography variant="h6" gutterBottom>
                      {news.title}
                    </Typography>
                    <Typography variant="body2">{news.description}</Typography>
                    {news.botton && (
                      <Box display="flex" justifyContent="end">
                        <BottomCustom
                          styleButton={{ mt: 2, width: 'fit-content' }}
                          text={news.botton.label}
                          onClick={() => navigate(news.botton.redirect)}
                        />
                      </Box>
                    )}
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>
    </Box>
  )
}

export default NewsSection
